/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement } from "@lexical/utils";
import type { EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread, TextFormatType } from "lexical";
import { $applyNodeReplacement, TextNode } from "lexical";

import { agreementVariablesList } from "./variables";

type SerializedVariableNode = Spread<
    {
        type: "variable";
        variableLabel: string;
    },
    SerializedTextNode
>;

/**
 * Variable node logic, used in contract settings, and contract validation modal (choose template)
 */
export class VariableNode extends TextNode {
    variableLabel: string = "";

    static getType(): string {
        return "variable";
    }

    static clone(node: VariableNode): VariableNode {
        return new VariableNode(node.__text, node.variableLabel, node.__key);
    }

    constructor(text: string, variableLabel: string, variableKey?: string, key?: NodeKey) {
        super(text, key);
        this.variableLabel = variableLabel;
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        addClassNamesToElement(element, config.theme.variable);
        element.setAttribute("data-text", this.__text);
        element.setAttribute("data-variable-name", this.variableLabel);
        element.setAttribute("title", this.__text);
        if (this.variableLabel) {
            // set variable label as text content : <html>{this.variableLabel}</html>
            element.textContent = this.variableLabel;
        }
        return element;
    }

    // TODO: Implement updateDOM so it removes the variable name if the variable doesn't match any name
    updateDOM(prevNode: any, dom: HTMLElement, config: EditorConfig): boolean {
        // Before we used to format labels like this -> ": nom du bénéficiaire"
        const correctedLabel = this.variableLabel?.replace(":", "");
        dom.setAttribute("data-variable-name", correctedLabel);
        dom.setAttribute("title", this.__text);
        // Only update variable label when value has changed. Before we showed {{name}} now we prefer -> nom du bénéficiaire
        if (prevNode.variableLabel !== correctedLabel) {
            dom.textContent = correctedLabel;
        }
        // Apply toggle styles from toolbar
        const format = Number(this.getFormat());
        dom.style.fontWeight = format === 1 ? "bold" : "lighter";

        return prevNode.variableLabel !== correctedLabel || prevNode.getFormat() !== format; // return true only if changes
    }

    static importJSON(serializedNode: SerializedVariableNode): VariableNode {
        const { text: variable } = serializedNode;
        const { variablesLabels } = agreementVariablesList;
        const variableLabel = serializedNode.variableLabel?.replace(":", "");

        let node = $createVariableNode(serializedNode.text, variableLabel);
        if (!variableLabel) {
            const variableLabel = variablesLabels[variable];
            node = $createVariableNode(serializedNode.text, variableLabel);
        }
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    // Persist data inside lexical node. This data is save at each JSON export of the editor (when saving agreement template)
    exportJSON(): SerializedVariableNode {
        return {
            ...super.exportJSON(),
            type: "variable",
            variableLabel: this.variableLabel,
        };
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    isTextEntity(): true {
        return true;
    }

    setFormat(format: number | TextFormatType): this {
        super.setFormat(format);
        return this;
    }

    getFormat(): number {
        return super.getFormat();
    }
}

/**
 * Generates a VariableNode, which is a string following the format of a # followed by some text, eg. #lexical.
 * @param text - The text used inside the VariableNode.
 * @param variableLabel - Full name of the variable
 * @returns - The VariableNode with the embedded text.
 */
export function $createVariableNode(text = "", variableLabel = ""): VariableNode {
    return $applyNodeReplacement(new VariableNode(text, variableLabel));
}

/**
 * Determines if node is a VariableNode.
 * @param node - The node to be checked.
 * @returns true if node is a VariableNode, false otherwise.
 */
export function $isVariableNode(node: LexicalNode | null | undefined): node is VariableNode {
    return node instanceof VariableNode;
}
